import { useEffect } from "react";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";

const SITE_NAME = "VendiDirect";
const SITE_URL = PUBLIC_SITE_URL;
const DEFAULT_TITLE = "VendiDirect - Il futuro dell'agenzia immobiliare con l'IA";
const DEFAULT_DESC = "Marketplace immobiliare e CRM avanzato. Trova lead dai social e dalle aste, genera valutazioni professionali e gestisci tutto il tuo ufficio con la potenza dell'intelligenza artificiale.";
const DEFAULT_IMAGE = `${SITE_URL}/vendidirect_og_luxury_gold_1200x630.png`;

export const TYPE_LABELS = {
    appartamento: "Appartamento", villa: "Villa", attico: "Attico", loft: "Loft", casa: "Casa",
    cantina: "Cantina", uffici: "Ufficio", locali_commerciali: "Locale commerciale", box_auto: "Box auto",
};

export const slugify = (s) => (s || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export const propertyPath = (p) => `/annunci/${p?.slug || p?.id}`;
export const cityPath = (cityOrSlug) => `/case-${slugify(cityOrSlug)}`;

export const propertySeoTitle = (p) => {
    const op = p.transaction_type === "affitto" ? "Affitto" : "Vendita";
    const tipo = TYPE_LABELS[p.property_type] || "Immobile";
    const base = `${op} ${tipo} a ${p.city || ""}`.trim();
    return p.zone ? `${base} - ${p.zone} | ${SITE_NAME}` : `${base} | ${SITE_NAME}`;
};

const upsertMeta = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => k !== "content" && el.setAttribute(k, v));
        document.head.appendChild(el);
    }
    el.setAttribute("content", attrs.content ?? "");
    return el;
};

const upsertLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
    el.setAttribute("href", href);
};

// Hook SEO unico per tutte le pagine: title/description univoci, canonical, Open Graph, robots e JSON-LD.
export function useSeo({ title, description, canonical, image, type = "website", noindex = false, jsonLd } = {}, deps = []) {
    useEffect(() => {
        const t = title || DEFAULT_TITLE;
        const d = (description || DEFAULT_DESC).replace(/\s+/g, " ").trim();
        const path = canonical || window.location.pathname;
        const url = String(path).startsWith("http") ? path : `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
        const img = image && String(image).startsWith("http") ? image : DEFAULT_IMAGE;
        document.title = t;
        upsertMeta('meta[name="description"]', { name: "description", content: d });
        upsertMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" });
        upsertLink("canonical", url);
        upsertMeta('meta[property="og:title"]', { property: "og:title", content: t });
        upsertMeta('meta[property="og:description"]', { property: "og:description", content: d });
        upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
        upsertMeta('meta[property="og:image"]', { property: "og:image", content: img });
        upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: img });
        upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: "Logo 3D Luxury Gold VendiDirect su sfondo scuro" });
        upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
        upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
        upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: t });
        upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: d });
        upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });

        document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
        const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean) : [];
        blocks.forEach((b) => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-seo-jsonld", "1");
            s.textContent = JSON.stringify({ "@context": "https://schema.org", ...b });
            document.head.appendChild(s);
        });
        return () => {
            document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
            document.title = DEFAULT_TITLE;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

export const breadcrumbLd = (items) => ({
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${SITE_URL}${it.path}` })),
});

export const propertyJsonLd = (p) => {
    const url = `${SITE_URL}${propertyPath(p)}`;
    const photos = (p.photos || []).filter((ph) => String(ph).startsWith("http")).slice(0, 5);
    const images = photos.length ? photos : [DEFAULT_IMAGE];
    const inStock = !p.status || p.status === "active";
    return [
        {
            "@type": "Product",
            "@id": `${url}#product`,
            name: propertySeoTitle(p).replace(" | VendiDirect", ""),
            description: (p.description || "").slice(0, 500),
            image: images,
            url,
            sku: p.id,
            category: p.transaction_type === "affitto" ? "Immobili in affitto" : "Immobili in vendita",
            brand: { "@type": "Brand", name: SITE_NAME },
            offers: {
                "@type": "Offer",
                url,
                price: p.price,
                priceCurrency: "EUR",
                availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                itemCondition: "https://schema.org/UsedCondition",
                seller: { "@type": p.owner_role === "privato" ? "Person" : "RealEstateAgent", name: p.owner_name || SITE_NAME },
                ...(p.transaction_type === "affitto" ? { priceSpecification: { "@type": "UnitPriceSpecification", price: p.price, priceCurrency: "EUR", unitCode: "MON" } } : {}),
            },
        },
        {
            "@type": "RealEstateListing",
            "@id": `${url}#listing`,
            name: p.title,
            url,
            image: images,
            datePosted: p.created_at,
            description: (p.description || "").slice(0, 500),
            mainEntity: {
                "@type": p.property_type === "villa" || p.property_type === "casa" ? "House" : "Apartment",
                name: p.title,
                numberOfRooms: p.rooms,
                numberOfBathroomsTotal: p.bathrooms,
                floorSize: { "@type": "QuantitativeValue", value: p.surface_sqm, unitCode: "MTK" },
                address: { "@type": "PostalAddress", addressLocality: p.city, addressRegion: p.region, addressCountry: "IT", ...(p.zone ? { streetAddress: p.zone } : {}) },
                ...(p.latitude && p.longitude && !p.locked ? { geo: { "@type": "GeoCoordinates", latitude: p.latitude, longitude: p.longitude } } : {}),
            },
        },
        breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Annunci", path: "/annunci" },
            { name: `Case a ${p.city}`, path: cityPath(p.city) },
            { name: p.title, path: propertyPath(p) },
        ]),
    ];
};

export const websiteJsonLd = () => [
    {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: "it-IT",
        potentialAction: { "@type": "SearchAction", target: `${SITE_URL}/annunci?city={search_term_string}`, "query-input": "required name=search_term_string" },
    },
    {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: DEFAULT_IMAGE,
        sameAs: ["https://www.instagram.com/vendidirectofficial", "https://www.tiktok.com/@vendidirectofficial"],
    },
];
